import { FC, useCallback, useRef, useState } from 'react';
import Modal from 'src/shared/Modal';
import color from 'src/theme/color';
import {
    AttachmentIconButton,
    AttachmentsContainer,
    Body,
    DescriptionText,
    ErrorMessage,
    Footer,
    HiddenFileInput,
    RichTextEditor
} from './styled';
import { MarkdownEditor } from 'src/shared/MarkdownEditor';
import QuestActionButton from 'src/pages/Quest/Review/components/ReviewButton';
import useAppDispatch from 'src/hooks/useAppDispatch';
import { postQuestHumanReview } from 'src/redux/quests/service';
import { setSnackbarAttributes } from 'src/redux/snackbar/slice';
import { ReactComponent as AttachmentIcon } from 'src/assets/icons/attachment.svg';
import useFileUploadWithValidation, {
    UploadedFile
} from 'src/hooks/useFileUploadWithValidation';
import { generateUniqueId } from 'src/utils/attachments';
import AttachmentItem from 'src/shared/AttachmentItem';
import UploadProgress from 'src/shared/UploadProgress';

interface Props {
    open: boolean;
    questId: string;
    onClose: () => void;
    onSuccess?: () => void;
}

const BeehiveReviewModal: FC<Props> = ({
    open,
    questId,
    onClose,
    onSuccess
}) => {
    const dispatch = useAppDispatch();

    const [description, setDescription] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [files, setFiles] = useState<UploadedFile[]>([]);
    const [fileError, setFileError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFilesSelect = useFileUploadWithValidation({
        onFilesUpdate: setFiles,
        onError: setFileError
    });

    const handleFileChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.target.files && e.target.files.length > 0) {
                const selectedFilesWithIds = Array.from(e.target.files).map(
                    (file) => ({
                        file,
                        id: generateUniqueId()
                    })
                );
                handleFilesSelect(selectedFilesWithIds);
            }
            if (e.target) {
                e.target.value = '';
            }
        },
        [handleFilesSelect]
    );

    const handleAttachmentClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const removeAttachment = useCallback((idToRemove: number): void => {
        setFiles((prev) =>
            prev.filter((file) => {
                if (file.fileId) {
                    return file.fileId !== idToRemove;
                }
                return file.id !== idToRemove;
            })
        );
    }, []);

    const cancelUpload = useCallback(
        (fileId: number): void => {
            const fileToCancel = files.find((f) => f.id === fileId);
            if (fileToCancel?.uploadController) {
                fileToCancel.uploadController.abort();
            }
            removeAttachment(fileId);
        },
        [files, removeAttachment]
    );

    const handleEditorChange = useCallback((value: string | undefined) => {
        setDescription(value || '');
    }, []);

    const resetForm = useCallback(() => {
        setDescription('');
        setFiles([]);
        setFileError(null);
        setIsLoading(false);
    }, []);

    const handleClose = useCallback(() => {
        resetForm();
        onClose();
    }, [onClose, resetForm]);

    const onSubmit = useCallback(() => {
        const hasUploadingFiles = files.some((f) => f.status === 'uploading');
        if (hasUploadingFiles) {
            setFileError(
                'Please wait for all uploads to complete before submitting'
            );
            return;
        }

        setIsLoading(true);

        const attachmentIds = files
            .filter((f) => f.fileId && f.status === 'completed')
            .map((f) => Number(f.fileId));

        dispatch(
            postQuestHumanReview({
                questId,
                params: {
                    feedback: description,
                    attachmentList: attachmentIds
                }
            })
        )
            .unwrap()
            .then(() => {
                dispatch(
                    setSnackbarAttributes({
                        snackbarOpen: true,
                        snackbarType: 'success',
                        snackbarMessage: "We'll handle your review shortly."
                    })
                );
                resetForm();
                onSuccess?.();
                onClose();
            })
            .catch(() => {
                dispatch(
                    setSnackbarAttributes({
                        snackbarOpen: true,
                        snackbarType: 'error',
                        snackbarMessage:
                            'An error occurred while sending your review request. Please try again.'
                    })
                );
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [dispatch, questId, description, files, onClose, onSuccess, resetForm]);

    const isDescriptionValid = description.trim().length > 0;
    const hasUploadingFiles = files.some((file) => file.status === 'uploading');

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title="Review by Beehive"
            hasCrossIcon
        >
            <Body>
                <DescriptionText>
                    Share any context the Beehive expert should know before
                    reviewing this request.
                </DescriptionText>

                <RichTextEditor>
                    <MarkdownEditor
                        value={description}
                        onChange={handleEditorChange}
                        height={240}
                    />
                </RichTextEditor>

                <HiddenFileInput
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx"
                    multiple
                    onChange={handleFileChange}
                />

                <AttachmentsContainer>
                    <AttachmentIconButton onClick={handleAttachmentClick}>
                        <AttachmentIcon color={color.lightningYellow} />
                    </AttachmentIconButton>
                    {files
                        .filter((file) => file.status === 'completed')
                        .map((attachedFile) => (
                            <AttachmentItem
                                key={`attachment-${attachedFile.id}`}
                                file={{
                                    id:
                                        Number(attachedFile.fileId) ||
                                        generateUniqueId(),
                                    name: attachedFile.file.name,
                                    type: attachedFile.file.type
                                }}
                                index={Number(attachedFile.fileId)}
                                removeAttachment={removeAttachment}
                            />
                        ))}
                </AttachmentsContainer>

                <UploadProgress files={files} onCancelUpload={cancelUpload} />

                {fileError && <ErrorMessage>{fileError}</ErrorMessage>}

                <Footer>
                    <QuestActionButton
                        label="Cancel"
                        onClick={handleClose}
                        width="120px"
                        styleType="codGray"
                        aria-label="Cancel the Beehive review request"
                    />
                    <QuestActionButton
                        label="Send"
                        onClick={onSubmit}
                        width="120px"
                        styleType="yellow"
                        aria-label="Send the review request to a Beehive expert"
                        disabled={!isDescriptionValid || hasUploadingFiles}
                        isLoading={isLoading}
                    />
                </Footer>
            </Body>
        </Modal>
    );
};

export default BeehiveReviewModal;
